import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { activationsApiRequest } from '@/lib/activationsApiHelpers';
import { useToast } from '@/hooks/use-toast';
import type {
  ActivationTemplateResponse,
  CreateActivationTemplateRequest,
  UpdateActivationTemplateRequest,
} from '@/types/generated/activations.generated';

const TEMPLATES_KEY = ['activation-templates'] as const;
const templateKey = (id: string) => ['activation-templates', id] as const;

/** Lists templates visible to the caller's org (own-org + global). */
export function useActivationTemplates() {
  return useQuery<ActivationTemplateResponse[]>({
    queryKey: TEMPLATES_KEY,
    queryFn: () => activationsApiRequest('GET', '/admin/activation-templates'),
  });
}

/** Gets a single visible template (own-org OR global). */
export function useActivationTemplate(id: string | undefined) {
  return useQuery<ActivationTemplateResponse>({
    queryKey: templateKey(id ?? ''),
    queryFn: () => activationsApiRequest('GET', `/admin/activation-templates/${id}`),
    enabled: !!id,
  });
}

export function useCreateActivationTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateActivationTemplateRequest) =>
      activationsApiRequest<ActivationTemplateResponse>('POST', '/admin/activation-templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
      toast({ title: 'Template created', description: 'The activation template has been created.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create template. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateActivationTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateActivationTemplateRequest }) =>
      activationsApiRequest<ActivationTemplateResponse>('PUT', `/admin/activation-templates/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
      queryClient.invalidateQueries({ queryKey: templateKey(variables.id) });
      toast({ title: 'Template updated', description: 'The activation template has been updated.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update template. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteActivationTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) =>
      activationsApiRequest('DELETE', `/admin/activation-templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
      toast({ title: 'Template deleted', description: 'The activation template has been removed.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.message ||
          'Failed to delete template. It may still be referenced by an activation.',
        variant: 'destructive',
      });
    },
  });
}
